import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Captions, CheckCircle2, Loader2, Play, RefreshCw, Video, VideoOff } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { CatalogMediaThumbnail } from '../content/ContentStudioShell';
import {
  getContentDraft,
  listContentItems,
  type ContentItemSummary,
  type ContentTypeRef,
} from '../content/adminContentApi';
import type { TextbookLine, TextbookMediaDocument } from '../qLanguage/adminQLanguageContentApi';

const TYPE: ContentTypeRef = { applicationKey: 'q-language', contentTypeKey: 'textbook-media' };

export type TranscriptCursor = { lineIndex: number; wordIndexes: number[] };
export type TranscriptSegment = { text: string; wordIndex: number | null };

export const findTranscriptCursor = (lines: TextbookLine[], timeMs: number): TranscriptCursor | null => {
  const lineIndex = lines.findIndex((line) => line.startMs <= timeMs && timeMs < line.endMs);
  if (lineIndex < 0) return null;
  const words = lines[lineIndex].words;
  if (!words?.length) return { lineIndex, wordIndexes: [] };
  const wordIndexes = words
    .map((word, wordIndex) => (word.startMs <= timeMs && timeMs < word.endMs ? wordIndex : -1))
    .filter((wordIndex) => wordIndex >= 0);
  return { lineIndex, wordIndexes };
};

export const transcriptSegments = (line: TextbookLine): TranscriptSegment[] => {
  const words = line.words;
  if (!words?.length) return [{ text: line.text, wordIndex: null }];
  let cursor = 0;
  const segments: TranscriptSegment[] = [];
  for (const [wordIndex, word] of words.entries()) {
    const valid = word.start >= cursor && word.endExclusive > word.start && word.endExclusive <= line.text.length
      && line.text.slice(word.start, word.endExclusive) === word.text;
    if (!valid) return [{ text: line.text, wordIndex: null }];
    if (word.start > cursor) segments.push({ text: line.text.slice(cursor, word.start), wordIndex: null });
    segments.push({ text: line.text.slice(word.start, word.endExclusive), wordIndex });
    cursor = word.endExclusive;
  }
  if (cursor < line.text.length) segments.push({ text: line.text.slice(cursor), wordIndex: null });
  return segments;
};

const errorMessage = (error: unknown): string =>
  error instanceof Error && error.message.trim() ? error.message : '교재 영상 목록을 불러오지 못했습니다.';

export function TextbookMediaCatalogItem({ item }: { item: ContentItemSummary }) {
  const attached = item.attributes['mediaStatus'] === 'ATTACHED';
  const thumbnailPath = item.attributes['thumbnailPath'];
  return (
    <div className="grid min-w-0 grid-cols-[7rem_minmax(0,1fr)] gap-3">
      <CatalogMediaThumbnail
        previewPath={attached && thumbnailPath ? thumbnailPath : null}
      />
      <div className="min-w-0">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 truncate text-sm font-semibold">{item.title}</span>
          {attached ? <Video className="h-4 w-4 shrink-0 text-emerald-600" /> : <VideoOff className="h-4 w-4 shrink-0 text-muted-foreground" />}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{item.key.slug} · {item.attributes['category']}</p>
      </div>
    </div>
  );
}

export default function QLanguageTextbookMediaPage({ embedded = false }: { embedded?: boolean }) {
  const [items, setItems] = useState<ContentItemSummary[]>([]);
  const [selected, setSelected] = useState<TextbookMediaDocument | null>(null);
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('');
  const [mediaStatus, setMediaStatus] = useState('ATTACHED');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transcriptCursor, setTranscriptCursor] = useState<TranscriptCursor | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await listContentItems(TYPE, {
        query: query || undefined,
        learningLanguage: language || undefined,
        mediaStatus: mediaStatus || undefined,
      });
      setItems(next);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setLoading(false);
    }
  }, [language, mediaStatus, query]);

  useEffect(() => { void loadItems(); }, [loadItems]);

  useEffect(() => {
    const video = videoRef.current;
    setTranscriptCursor(null);
    if (!video || !selected) return undefined;

    let animationFrame: number | null = null;
    const syncCursor = () => {
      const next = findTranscriptCursor(selected.lesson.lines, video.currentTime * 1_000);
      setTranscriptCursor((current) => (
        current?.lineIndex === next?.lineIndex
          && current?.wordIndexes.length === next?.wordIndexes.length
          && current?.wordIndexes.every((wordIndex, index) => wordIndex === next?.wordIndexes[index])
          ? current
          : next
      ));
    };
    const stopTracking = () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      animationFrame = null;
      syncCursor();
    };
    const trackPlayback = () => {
      syncCursor();
      animationFrame = video.paused || video.ended ? null : requestAnimationFrame(trackPlayback);
    };
    const startTracking = () => {
      if (animationFrame === null) animationFrame = requestAnimationFrame(trackPlayback);
    };

    video.addEventListener('play', startTracking);
    video.addEventListener('pause', stopTracking);
    video.addEventListener('ended', stopTracking);
    video.addEventListener('seeking', syncCursor);
    video.addEventListener('timeupdate', syncCursor);
    video.addEventListener('loadedmetadata', syncCursor);
    syncCursor();
    if (!video.paused) startTracking();

    return () => {
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      video.removeEventListener('play', startTracking);
      video.removeEventListener('pause', stopTracking);
      video.removeEventListener('ended', stopTracking);
      video.removeEventListener('seeking', syncCursor);
      video.removeEventListener('timeupdate', syncCursor);
      video.removeEventListener('loadedmetadata', syncCursor);
    };
  }, [selected]);

  const open = async (lessonId: string) => {
    setWorking(true);
    setError(null);
    try {
      const view = await getContentDraft<TextbookMediaDocument>(TYPE, lessonId);
      setSelected(view.document);
    } catch (failure) {
      setError(errorMessage(failure));
    } finally {
      setWorking(false);
    }
  };

  const attachedCount = useMemo(
    () => items.filter((item) => item.attributes['mediaStatus'] === 'ATTACHED').length,
    [items],
  );
  const transcriptStats = useMemo(() => {
    if (!selected) return null;
    const alignedLines = selected.lesson.lines.filter((line) => line.words?.length).length;
    const alignedWords = selected.lesson.lines.reduce((count, line) => count + (line.words?.length ?? 0), 0);
    return { alignedLines, alignedWords, totalLines: selected.lesson.lines.length };
  }, [selected]);
  const seekTo = (timeMs: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = timeMs / 1_000;
    setTranscriptCursor(selected ? findTranscriptCursor(selected.lesson.lines, timeMs) : null);
  };
  const Title = embedded ? 'h2' : 'h1';

  return (
    <div className="w-full min-w-0 space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Title className="text-2xl font-bold">Q Language 교재 영상</Title>
          <p className="mt-1 text-sm text-muted-foreground">Grok 생성 영상과 Qwen 정렬 word span·WebVTT 자막을 LIBRARY_ONLY 교재 대본 계보로 함께 확인합니다.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">현재 목록 영상 {attachedCount}개</Badge>
          <Button variant="outline" onClick={() => void loadItems()} disabled={loading}>
            <RefreshCw className="mr-2 h-4 w-4" />새로고침
          </Button>
        </div>
      </div>

      {error && <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{error}</div>}

      <div className="grid min-w-0 gap-5 xl:grid-cols-[20rem_minmax(0,1fr)]">
        <aside className="min-w-0 space-y-3 xl:sticky xl:top-4 xl:self-start">
          <Card><CardContent className="grid gap-2 p-4">
            <Input aria-label="교재 검색" placeholder="교재 제목·ID 검색" value={query} onChange={(event) => setQuery(event.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <select aria-label="교재 언어" className="h-9 rounded-md border bg-background px-2 text-sm" value={language} onChange={(event) => setLanguage(event.target.value)}>
                <option value="">모든 언어</option><option value="ko">한국어</option><option value="en">영어</option>
              </select>
              <select aria-label="영상 연결" className="h-9 rounded-md border bg-background px-2 text-sm" value={mediaStatus} onChange={(event) => setMediaStatus(event.target.value)}>
                <option value="">전체</option><option value="ATTACHED">영상 있음</option><option value="MISSING">영상 없음</option>
              </select>
            </div>
          </CardContent></Card>
          <div className="grid max-h-[70vh] gap-2 overflow-y-auto pr-1">
            {loading ? <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div> : items.map((item) => (
              <button key={item.key.slug} type="button" onClick={() => void open(item.key.slug)} className={`min-w-0 rounded-lg border p-2 text-left transition hover:bg-muted ${selected?.lesson.id === item.key.slug ? 'border-primary bg-primary/5' : ''}`}>
                <TextbookMediaCatalogItem item={item} />
              </button>
            ))}
          </div>
        </aside>

        <main className="min-w-0">
          {!selected ? (
            <Card><CardContent className="flex min-h-96 flex-col items-center justify-center text-center">
              <Play className="h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-semibold">왼쪽에서 교재 영상을 선택하세요</p>
              <p className="mt-1 text-sm text-muted-foreground">영상, 실제 대사, 연결 해시를 한 화면에서 확인할 수 있습니다.</p>
            </CardContent></Card>
          ) : (
            <div className="grid min-w-0 gap-5">
              <Card className="overflow-hidden">
                {selected.media ? (
                  <video ref={videoRef} key={`${selected.media.sha256}-${selected.captions.sha256}`} className="aspect-video w-full bg-black object-contain" controls preload="metadata" playsInline src={selected.media.playbackPath}>
                    <track kind="subtitles" src={selected.captions.playbackPath} srcLang={selected.lesson.languageTag} label={`${selected.lesson.languageTag.toUpperCase()} WebVTT`} default />
                  </video>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center bg-muted"><VideoOff className="h-10 w-10 text-muted-foreground" /></div>
                )}
                <CardHeader>
                  <div className="flex flex-wrap items-center gap-2"><Badge>{selected.lesson.languageTag}</Badge><Badge variant="outline">{selected.lesson.category}</Badge><Badge variant="secondary">LIBRARY_ONLY</Badge><Badge variant="outline"><Captions className="mr-1 h-3 w-3" />WebVTT</Badge>{transcriptStats && <Badge variant="outline">word span {transcriptStats.alignedLines}/{transcriptStats.totalLines}줄 · {transcriptStats.alignedWords}단어</Badge>}</div>
                  <CardTitle>{selected.lesson.title}</CardTitle>
                  <CardDescription>{selected.lesson.gloss}</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm">
                  <div className="rounded-lg border bg-muted/30 p-3">
                    <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4 text-emerald-600" />교재 대본 해시를 영상 메타데이터에 고정함</p>
                    <p className="mt-2 break-all text-xs text-muted-foreground">대본 {selected.scriptSha256}</p>
                    <p className="mt-1 break-all text-xs text-muted-foreground">자막 {selected.captions.sha256} · {(selected.captions.byteLength / 1024).toFixed(1)}KB · {selected.captions.captionKey}</p>
                    {selected.media && <p className="mt-1 break-all text-xs text-muted-foreground">영상 {selected.media.sha256} · {(selected.media.byteLength / 1024 / 1024).toFixed(1)}MB · {selected.media.generationProvider} 세션 {selected.media.generationSessionId} · {selected.media.processingPipeline}</p>}
                  </div>
                  <p className="text-xs leading-5 text-muted-foreground">현재 데일리 미션에는 연결하지 않았습니다. 동일 장면이 아니라 동일 대본 해시와 명시적 미션 버전이 확인될 때만 승격됩니다.</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base"><BookOpen className="h-4 w-4" />시간 동기화 원본 대화</CardTitle>
                  <CardDescription>재생 중인 대사와 Qwen word span을 강조합니다. 타임코드나 단어를 누르면 해당 위치로 이동합니다.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3">
                  {selected.lesson.lines.map((line, lineIndex) => {
                    const activeLine = transcriptCursor?.lineIndex === lineIndex;
                    return (
                      <div key={`${line.speaker}-${lineIndex}`} className={`rounded-lg border p-3 transition-colors ${activeLine ? 'border-primary bg-primary/5' : ''}`}>
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs font-semibold text-muted-foreground">{line.speaker}</p>
                          <button type="button" className="rounded px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground hover:bg-muted" onClick={() => seekTo(line.startMs)}>
                            {(line.startMs / 1_000).toFixed(2)}s
                          </button>
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-7">
                          {transcriptSegments(line).map((segment, segmentIndex) => segment.wordIndex === null ? (
                            <React.Fragment key={`text-${segmentIndex}`}>{segment.text}</React.Fragment>
                          ) : (
                            <button
                              key={`word-${segment.wordIndex}`}
                              type="button"
                              className={`inline rounded-sm px-0.5 transition-colors ${activeLine && transcriptCursor?.wordIndexes.includes(segment.wordIndex) ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                              onClick={() => seekTo(line.words![segment.wordIndex!].startMs)}
                            >
                              {segment.text}
                            </button>
                          ))}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">{line.gloss}</p>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          )}
        </main>
      </div>
      {working && <div className="fixed bottom-5 right-5 flex items-center gap-2 rounded-full border bg-background px-4 py-2 text-sm shadow"><Loader2 className="h-4 w-4 animate-spin" />교재 여는 중</div>}
    </div>
  );
}
